import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { Menu, X } from "lucide-react";

type ClassValue = string | false | null | undefined;

function cn(...values: ClassValue[]) {
  return values.filter(Boolean).join(" ");
}

type AsChildProps = {
  asChild?: boolean;
  children?: ReactNode;
  className?: string;
  onClick?: (event: unknown) => void;
  [key: string]: unknown;
};

function Primitive({ asChild, children, className, ...props }: AsChildProps) {
  if (asChild && isValidElement(children)) {
    const child = Children.only(children) as ReactElement<Record<string, unknown>>;
    const childProps = child.props;
    return cloneElement(child, {
      ...props,
      ...childProps,
      className: cn(className, String(childProps.className || "")),
      onClick: (event: unknown) => {
        (props.onClick as ((event: unknown) => void) | undefined)?.(event);
        (childProps.onClick as ((event: unknown) => void) | undefined)?.(event);
      },
    });
  }
  return <span className={className} {...props}>{children}</span>;
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "outline" | "secondary" | "ghost";
  size?: "default" | "sm" | "icon" | "icon-sm";
  asChild?: boolean;
};

export function Button({ className, variant = "default", size = "default", asChild, children, type = "button", ...props }: ButtonProps) {
  if (asChild) {
    return <Primitive asChild className={cn("nexus-button", className)} data-slot="button" data-variant={variant} data-size={size} {...props}>{children}</Primitive>;
  }
  return <button type={type} className={cn("nexus-button", className)} data-slot="button" data-variant={variant} data-size={size} {...props}>{children}</button>;
}

export function Badge({ className, variant = "default", ...props }: HTMLAttributes<HTMLSpanElement> & { variant?: "default" | "outline" | "secondary" }) {
  return <span className={cn("nexus-badge", className)} data-slot="badge" data-variant={variant} {...props} />;
}

export function Progress({ value = 0, className, ...props }: HTMLAttributes<HTMLDivElement> & { value?: number }) {
  const safeValue = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className={cn("nexus-progress", className)} data-slot="progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={safeValue} {...props}>
      <span data-slot="progress-indicator" style={{ transform: `translateX(-${100 - safeValue}%)` }} />
    </div>
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn("nexus-input", className)} data-slot="input" {...props} />;
}

type SliderProps = Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> & {
  value: number[];
  onValueChange: (value: number[]) => void;
  max?: number;
  min?: number;
  step?: number;
};

export function Slider({ value, onValueChange, max = 100, min = 0, step = 1, className, ...props }: SliderProps) {
  const current = Math.max(min, Math.min(max, value[0] ?? min));
  const percentage = max === min ? 0 : ((current - min) / (max - min)) * 100;
  return (
    <div className={cn("nexus-slider", className)} data-slot="slider">
      <span data-slot="slider-track"><span data-slot="slider-range" style={{ width: `${percentage}%` }} /></span>
      <span data-slot="slider-thumb" style={{ left: `${percentage}%` }} />
      <input type="range" min={min} max={max} step={step} value={current} onChange={(event) => onValueChange([Number(event.target.value)])} {...props} />
    </div>
  );
}

type ToggleContextValue = { value: string; setValue: (value: string) => void };
const ToggleContext = createContext<ToggleContextValue | null>(null);

export function ToggleGroup({ value, onValueChange, className, children, ...props }: HTMLAttributes<HTMLDivElement> & { type?: "single"; value: string; onValueChange: (value: string) => void }) {
  const context = useMemo(() => ({ value, setValue: onValueChange }), [value, onValueChange]);
  return <ToggleContext.Provider value={context}><div className={className} data-slot="toggle-group" {...props}>{children}</div></ToggleContext.Provider>;
}

export function ToggleGroupItem({ value, className, children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { value: string }) {
  const context = useContext(ToggleContext);
  const active = context?.value === value;
  return <button type="button" className={cn("nexus-toggle-item", className)} data-slot="toggle-group-item" data-state={active ? "on" : "off"} aria-pressed={active} onClick={() => context?.setValue(value)} {...props}>{children}</button>;
}

type TabsContextValue = { value: string; setValue: (value: string) => void; baseId: string };
const TabsContext = createContext<TabsContextValue | null>(null);

export function Tabs({ defaultValue, value: controlledValue, onValueChange, className, children, ...props }: HTMLAttributes<HTMLDivElement> & { defaultValue?: string; value?: string; onValueChange?: (value: string) => void }) {
  const [internalValue, setInternalValue] = useState(defaultValue || "");
  const baseId = useId();
  const value = controlledValue ?? internalValue;
  const setValue = onValueChange ?? setInternalValue;
  const context = useMemo(() => ({ value, setValue, baseId }), [value, setValue, baseId]);
  return <TabsContext.Provider value={context}><div className={className} data-slot="tabs" {...props}>{children}</div></TabsContext.Provider>;
}

export function TabsList({ className, variant, ...props }: HTMLAttributes<HTMLDivElement> & { variant?: string }) {
  return <div className={cn("nexus-tabs-list", className)} data-slot="tabs-list" data-variant={variant} role="tablist" {...props} />;
}

export function TabsTrigger({ value, className, children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { value: string }) {
  const context = useContext(TabsContext);
  const active = context?.value === value;
  return <button type="button" className={cn("nexus-tabs-trigger", className)} data-slot="tabs-trigger" data-state={active ? "active" : "inactive"} role="tab" aria-selected={active} aria-controls={`${context?.baseId}-${value}`} onClick={() => context?.setValue(value)} {...props}>{children}</button>;
}

export function TabsContent({ value, className, ...props }: HTMLAttributes<HTMLDivElement> & { value: string }) {
  const context = useContext(TabsContext);
  if (context?.value !== value) return null;
  return <div className={className} data-slot="tabs-content" id={`${context.baseId}-${value}`} role="tabpanel" {...props} />;
}

const modalStack: HTMLElement[] = [];
let bodyOverflowBeforeModal = "";

function useModalFocus(ref: RefObject<HTMLElement | null>, open: boolean, onClose: () => void) {
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; }, [onClose]);
  useEffect(() => {
    const element = ref.current;
    if (!open || !element) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!modalStack.length) {
      bodyOverflowBeforeModal = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    modalStack.push(element);
    const focusable = () => Array.from(element.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')).filter((node) => node.tabIndex >= 0 && node.getClientRects().length && !node.closest('[inert]'));
    const focusFirst = () => (focusable()[0] ?? element).focus();
    focusFirst();
    const onKeyDown = (event: KeyboardEvent) => {
      if (modalStack.at(-1) !== element) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
      }
      if (event.key !== "Tab") return;
      const targets = focusable();
      const first = targets[0];
      const last = targets.at(-1);
      if (!first) { event.preventDefault(); element.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === element)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    const onFocus = (event: FocusEvent) => {
      if (modalStack.at(-1) === element && event.target instanceof Node && !element.contains(event.target)) focusFirst();
    };
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("focusin", onFocus);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("focusin", onFocus);
      const index = modalStack.indexOf(element);
      if (index !== -1) modalStack.splice(index, 1);
      const top = modalStack.at(-1);
      if (!top) document.body.style.overflow = bodyOverflowBeforeModal;
      if (previousFocus?.isConnected && (!top || top.contains(previousFocus))) previousFocus.focus();
    };
  }, [open, ref]);
}

type SheetContextValue = { open: boolean; setOpen: (open: boolean) => void; titleId: string; descriptionId: string };
const SheetContext = createContext<SheetContextValue | null>(null);

export function Sheet({ open: controlledOpen, onOpenChange, children }: { open?: boolean; onOpenChange?: (open: boolean) => void; children: ReactNode }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const baseId = useId();
  const context = useMemo(() => ({ open, setOpen, titleId: `${baseId}-title`, descriptionId: `${baseId}-description` }), [open, setOpen, baseId]);
  return <SheetContext.Provider value={context}>{children}</SheetContext.Provider>;
}

export function SheetTrigger({ asChild, children, ...props }: AsChildProps) {
  const context = useContext(SheetContext);
  return <Primitive asChild={asChild} data-slot="sheet-trigger" onClick={() => context?.setOpen(true)} {...props}>{children}</Primitive>;
}

export function SheetClose({ asChild, children, ...props }: AsChildProps) {
  const context = useContext(SheetContext);
  return <Primitive asChild={asChild} data-slot="sheet-close" onClick={() => context?.setOpen(false)} {...props}>{children}</Primitive>;
}

export function SheetContent({ className, children, showCloseButton = true, ...props }: HTMLAttributes<HTMLDivElement> & { showCloseButton?: boolean }) {
  const context = useContext(SheetContext);
  const contentRef = useRef<HTMLElement>(null);
  useModalFocus(contentRef, Boolean(context?.open), () => context?.setOpen(false));
  if (!context?.open || typeof document === "undefined") return null;
  return createPortal(
    <div className="nexus-sheet-layer" data-slot="sheet-portal">
      <button type="button" className="nexus-sheet-overlay" tabIndex={-1} aria-label="Schließen" onClick={() => context.setOpen(false)} />
      <section ref={contentRef} tabIndex={-1} className={cn("nexus-sheet-content", className)} data-slot="sheet-content" role="dialog" aria-modal="true" aria-labelledby={context.titleId} aria-describedby={context.descriptionId} {...props}>
        {showCloseButton ? <button type="button" className="nexus-sheet-close" aria-label="Schließen" onClick={() => context.setOpen(false)}><X /></button> : null}
        {children}
      </section>
    </div>,
    document.body,
  );
}

export function SheetHeader(props: HTMLAttributes<HTMLDivElement>) { return <div data-slot="sheet-header" {...props} />; }
export function SheetTitle(props: HTMLAttributes<HTMLHeadingElement>) { const context = useContext(SheetContext); return <h2 id={context?.titleId} data-slot="sheet-title" {...props} />; }
export function SheetDescription(props: HTMLAttributes<HTMLParagraphElement>) { const context = useContext(SheetContext); return <p id={context?.descriptionId} data-slot="sheet-description" {...props} />; }

export function TooltipProvider({ children }: { children: ReactNode; delayDuration?: number }) { return children; }
export function Tooltip({ children }: { children: ReactNode }) { return <span className="nexus-tooltip">{children}</span>; }
export function TooltipTrigger({ asChild, children, ...props }: AsChildProps) { return <Primitive asChild={asChild} {...props}>{children}</Primitive>; }
export function TooltipContent({ children, className, side: _side, ...props }: HTMLAttributes<HTMLSpanElement> & { side?: string }) { return <span className={cn("nexus-tooltip-content", className)} role="tooltip" {...props}>{children}</span>; }

type SidebarContextValue = { open: boolean; setOpen: (open: boolean) => void; mobile: boolean; id: string };
const SidebarContext = createContext<SidebarContextValue | null>(null);

export function SidebarProvider({ defaultOpen = true, children, className }: { defaultOpen?: boolean; children: ReactNode; className?: string }) {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 820px)").matches);
  const [open, setOpen] = useState(defaultOpen && !mobile);
  const id = useId();
  useEffect(() => {
    const media = window.matchMedia("(max-width: 820px)");
    const onChange = () => { setMobile(media.matches); setOpen(!media.matches && defaultOpen); };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [defaultOpen]);
  const context = useMemo(() => ({ open, setOpen, mobile, id }), [open, mobile, id]);
  return <SidebarContext.Provider value={context}><div className={cn("nexus-sidebar-layout", className)} data-sidebar-open={open}>{mobile && open ? <button type="button" tabIndex={-1} className="nexus-sidebar-backdrop" aria-label="Navigation schließen" onClick={() => setOpen(false)}/> : null}{children}</div></SidebarContext.Provider>;
}

export function Sidebar({ className, children }: HTMLAttributes<HTMLElement> & { collapsible?: string }) {
  const context = useContext(SidebarContext);
  const sidebarRef = useRef<HTMLElement>(null);
  const isModal = Boolean(context?.mobile && context.open);
  useModalFocus(sidebarRef, isModal, () => context?.setOpen(false));
  return <aside ref={sidebarRef} id={context?.id} tabIndex={-1} role={isModal ? "dialog" : undefined} aria-modal={isModal ? true : undefined} aria-label="HQS Navigation" inert={context?.mobile && !context.open ? true : undefined} className={cn("nexus-sidebar", className)} data-sidebar="sidebar" data-state={context?.open ? "expanded" : "collapsed"}>{isModal ? <button type="button" className="nexus-sidebar-close" aria-label="Navigation schließen" onClick={() => context?.setOpen(false)}><X/></button> : null}{children}</aside>;
}
export function SidebarInset(props: HTMLAttributes<HTMLElement>) { return <main data-slot="sidebar-inset" {...props} />; }
export function SidebarHeader(props: HTMLAttributes<HTMLDivElement>) { return <div data-slot="sidebar-header" {...props} />; }
export function SidebarContent(props: HTMLAttributes<HTMLDivElement>) { return <div data-slot="sidebar-content" {...props} />; }
export function SidebarFooter(props: HTMLAttributes<HTMLDivElement>) { return <div data-slot="sidebar-footer" {...props} />; }
export function SidebarGroup(props: HTMLAttributes<HTMLDivElement>) { return <section data-slot="sidebar-group" {...props} />; }
export function SidebarGroupContent(props: HTMLAttributes<HTMLDivElement>) { return <div data-slot="sidebar-group-content" {...props} />; }
export function SidebarGroupLabel(props: HTMLAttributes<HTMLDivElement>) { return <div data-slot="sidebar-group-label" {...props} />; }
export function SidebarMenu(props: HTMLAttributes<HTMLUListElement>) { return <ul data-slot="sidebar-menu" {...props} />; }
export function SidebarMenuItem(props: HTMLAttributes<HTMLLIElement>) { return <li data-slot="sidebar-menu-item" {...props} />; }
export function SidebarMenuButton({ isActive, tooltip, className, onClick, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { isActive?: boolean; tooltip?: string }) {
  const context = useContext(SidebarContext);
  return <button type="button" className={className} title={tooltip} aria-label={tooltip} aria-current={isActive ? "page" : undefined} data-slot="sidebar-menu-button" data-active={isActive ? "true" : "false"} onClick={(event) => { onClick?.(event); if (!event.defaultPrevented && context?.mobile) context.setOpen(false); }} {...props} />;
}
export function SidebarSeparator(props: HTMLAttributes<HTMLHRElement>) { return <hr data-slot="sidebar-separator" {...props} />; }
export function SidebarRail(props: ButtonHTMLAttributes<HTMLButtonElement>) { const context = useContext(SidebarContext); return <button type="button" className="nexus-sidebar-rail" aria-label="Navigation einklappen" onClick={() => context?.setOpen(!context.open)} {...props} />; }
export function SidebarTrigger({ className, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) { const context = useContext(SidebarContext); return <button type="button" className={className} data-slot="sidebar-trigger" aria-label="Navigation umschalten" aria-expanded={context?.open} aria-controls={context?.id} onClick={() => context?.setOpen(!context.open)} {...props}><Menu /></button>; }
